// This file is automatically generated. Do not edit it directly.
// Server-side Supabase client with service role key - bypasses RLS.
// Use this for admin operations in server functions and server routes only.
// For user-authenticated queries (with RLS), use the auth middleware instead.
import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";

function createSupabaseAdminClient() {
  // APP_* variants take precedence: the owner-managed Supabase project (2026-07
  // migration off Lovable Cloud). The override is all-or-nothing — a URL from one
  // project paired with a key from another would authenticate nothing (or worse,
  // half-work: reads pass via public RLS policies while every write dies).
  const useOverride = !!(process.env.APP_SUPABASE_URL && process.env.APP_SUPABASE_SERVICE_ROLE_KEY);
  const SUPABASE_URL = useOverride ? process.env.APP_SUPABASE_URL : process.env.SUPABASE_URL;
  const SUPABASE_SERVICE_ROLE_KEY = useOverride
    ? process.env.APP_SUPABASE_SERVICE_ROLE_KEY
    : process.env.SUPABASE_SERVICE_ROLE_KEY;

  if (!SUPABASE_URL || !SUPABASE_SERVICE_ROLE_KEY) {
    const missing = [
      ...(!SUPABASE_URL ? ["SUPABASE_URL"] : []),
      ...(!SUPABASE_SERVICE_ROLE_KEY ? ["SUPABASE_SERVICE_ROLE_KEY"] : []),
    ];
    const message = `Missing Supabase environment variable(s): ${missing.join(", ")}. Connect Supabase in Lovable Cloud.`;
    console.error(`[Supabase] ${message}`);
    throw new Error(message);
  }

  return createClient<Database>(SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY, {
    auth: {
      storage: undefined,
      persistSession: false,
      autoRefreshToken: false,
    },
    global: {
      // 2026-07-02 incident hardening: a wedged/slow database once hung SSR forever
      // (no response ever streamed — the tab just spun). Cap every server-side
      // PostgREST call so a dead DB surfaces as an error (which callers already
      // fail-soft on) instead of an infinite hang. Callers that pass their own
      // AbortSignal keep it.
      fetch: (url, init) =>
        fetch(url, { ...init, signal: init?.signal ?? AbortSignal.timeout(10_000) }),
    },
  });
}

let _supabaseAdmin: ReturnType<typeof createSupabaseAdminClient> | undefined;

// Server-side Supabase client with service role - bypasses RLS
// SECURITY: Only use this for trusted server-side operations, never expose to client code
// Import like: import { supabaseAdmin } from "@/integrations/supabase/client.server";
export const supabaseAdmin = new Proxy({} as ReturnType<typeof createSupabaseAdminClient>, {
  get(_, prop, receiver) {
    if (!_supabaseAdmin) _supabaseAdmin = createSupabaseAdminClient();
    return Reflect.get(_supabaseAdmin, prop, receiver);
  },
});
